import { useCallback } from 'react';
import { unstable_serialize as serialize, useSWRConfig } from 'swr';

import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import {
	buildPromoMessageKey,
	PromoIconResponse,
	promotionDetailsFetcher,
} from '@/data/Content/PromoMessage';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';

type GetPromotionDetailsProps = {
	catentryId: string;
};

export type PromotionDetails = PromoIconResponse;

export const useGetPromotionDetails = () => {
	const { cache, mutate } = useSWRConfig();
	const router = useNextRouter();
	const { settings } = useSettings();
	const { physicalStoreId: currentPhysicalStoreId } = useCurrentPhysicalStoreId();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const extraRequestParameters = useExtraRequestParameters();

	return useCallback(
		async ({ catentryId }: GetPromotionDetailsProps): Promise<PromotionDetails | null> => {
			if (!currentPhysicalStoreId || !storeId) return null;

			const { marketId: currentMarketId } = getStoreDataByPhysicalStoreId(currentPhysicalStoreId);

			const query = {
				catentryId,
				currentPhysicalStoreId,
				currentMarketId,
				profileName: 'HDM_findDisplayPromotionByCatentryId',
				langId,
				responseFormat: 'json',
			} as const;

			const key = buildPromoMessageKey({
				storeId,
				...query,
			});
			const serializedKey = serialize(key);
			const cachedData = cache.get(serializedKey)?.data as PromoIconResponse | undefined;

			if (cachedData) {
				return cachedData;
			}

			const fetcher = async () => {
				const response = await promotionDetailsFetcher(true)(
					storeId,
					query,
					extraRequestParameters
				);

				if (!response) return null;

				return response;
			};

			const data = await fetcher();

			await mutate(key, data, { revalidate: false });

			return data;
		},
		[cache, currentPhysicalStoreId, extraRequestParameters, langId, mutate, storeId]
	);
};
